import React, { useState } from 'react'
import { useAtom } from 'jotai'
import { useTheme } from 'react-jss'
import { Root, Trigger, Overlay, Content, Portal } from '@radix-ui/react-dialog'
import { Button } from '../../../ui/Button/Button'
import { useStyles } from '../../../ui/Dialog/Dialog'
import { removeMergedCase } from '../../../../api/salary'
import { colors } from '../../../../theme/palette'
import { IkonFjernsammenlaegning } from '../../../../assets/icons'
import { CaseDetail, MergedCaseApiData } from '../../../../models/CaseDetail'
import { L } from '../../../providers/Lokalise'
import { CircleIcon2 } from '../../../ui/Circle/Circle'
import Title from '../../../ui/Dialog/Title'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { ErrorDisplay } from '../Contact/ContactDialog'
import { profileAtom } from '../../../../api/profile/profile'
import { Tooltip } from '../../../ui/Tooltip/Tooltip'

interface RemoveMergedCaseProps {
    currentCase: CaseDetail
    otherCase: MergedCaseApiData
    onSuccess?: () => void
}

export const RemoveMergedCase: React.FC<RemoveMergedCaseProps> = ({
    currentCase,
    otherCase,
    onSuccess,
}) => {
    const [authProfile] = useAtom(profileAtom)
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.red200
    const iconBorder = colors.white
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const handleRemoveMergedCase = async (): Promise<void> => {
        setIsLoading(true)
        const response = await removeMergedCase({
            caseId: currentCase.caseId,
            caseRegTime: currentCase.caseRegistrationTime,
            otherCaseId: otherCase.sagsId,
            otherCaseRegTime: otherCase.sagsRegTid,
        })
        if (response.error) {
            showError(<ErrorDisplay errors={response.error.errors} />)
            setIsLoading(false)
            return
        }
        setIsLoading(false)
        setIsOpen(false)
        onSuccess?.()
    }

    return (
        <>
            <Root open={isOpen}>
                {currentCase.allowedUpdateActionList.includes(
                    'FjernSammenfletMedAndenSag'
                ) &&
                    !authProfile.isImitator && (
                        <Trigger asChild>
                            <Button
                                type="action"
                                onClick={() => setIsOpen(true)}
                                disabled={
                                    !currentCase.allowedUpdateActionList.includes(
                                        'FjernSammenfletMedAndenSag'
                                    )
                                }
                            >
                                <CircleIcon2
                                    backgroundColor={colors.red100}
                                    width="26px"
                                >
                                    <IkonFjernsammenlaegning
                                        width="14px"
                                        fill={colors.red600}
                                    />
                                </CircleIcon2>
                            </Button>
                        </Trigger>
                    )}
                {(authProfile.isImitator ||
                    !currentCase.allowedUpdateActionList.includes(
                        'FjernSammenfletMedAndenSag'
                    )) && (
                    <Button
                        type="action"
                        disabled={
                            !currentCase.allowedUpdateActionList.includes(
                                'SammenfletMedAndenSag'
                            )
                        }
                        style={{
                            cursor: 'not-allowed',
                            opacity: 0.5,
                        }}
                    >
                        <CircleIcon2
                            backgroundColor={colors.red100}
                            width="26px"
                        >
                            <IkonFjernsammenlaegning
                                width="14px"
                                fill={colors.red600}
                            />
                        </CircleIcon2>
                    </Button>
                )}
                <Portal>
                    <Overlay className={classes.overlay} />
                    <Content className={`${classes.overlayContainer}`}>
                        <div className={`${classes.container} withIcon`}>
                            <div className={`${classes.circleIcon} withIcon`}>
                                <IkonFjernsammenlaegning
                                    width="46px"
                                    fill={colors.red500}
                                />
                            </div>
                            <Title
                                style={{
                                    marginBottom: '3rem',
                                    textAlign: 'center',
                                }}
                                title={L(
                                    'LØN_FJERN_SAMMENLAGT_SAG_DIALOG_TITEL'
                                )}
                            />
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <Button
                                    type="secondary"
                                    size="xlarge"
                                    onClick={() => setIsOpen(false)}
                                    className="ignore-clickoutside"
                                >
                                    {L(
                                        'LØN_FJERN_SAMMENLAGT_SAG_DIALOG_FORTRYD_KNAP'
                                    )}
                                </Button>
                                <Button
                                    type="danger"
                                    size="xlarge"
                                    onClick={handleRemoveMergedCase}
                                    loading={isLoading}
                                    className="ignore-clickoutside"
                                >
                                    {L(
                                        'LØN_FJERN_SAMMENLAGT_SAG_DIALOG_FJERN_KNAP'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Content>
                </Portal>
            </Root>
            <ErrorDialogComponent />
        </>
    )
}
